import React, { useState, useEffect } from 'react';
import { X, Clock, DollarSign, CheckCircle, XCircle, ChevronDown, ChevronRight, Zap, Mic, Image, FileText } from 'lucide-react';
import { useTrace } from '../hooks/useTraces';
import { Skeleton } from './ui/skeleton';

function extractModelFromTrace(trace) {
  if (trace?.root_model && trace.root_model !== 'unknown') {
    return { model: trace.root_model, provider: trace.root_provider };
  }
  if (trace?.spans) {
    for (const span of trace.spans) {
      const model = span.attributes?.model || span.attributes?.['gen_ai.request.model'];
      if (model && model !== 'unknown') {
        return { model, provider: span.attributes?.provider || span.attributes?.['gen_ai.system'] || null };
      }
    }
  }
  return { model: null, provider: null };
}

function SpanItem({ span, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isSuccess = span.status_code !== 'ERROR';
  const model = span.attributes?.model || span.attributes?.['gen_ai.request.model'];
  const latency = span.duration_ms ? (span.duration_ms / 1000).toFixed(2) : null;
  const cost = span.attributes?.cost || span.attributes?.['gen_ai.usage.cost'];

  return (
    <div className={`p-3 rounded-lg transition-colors ${expanded ? 'bg-[#141416]' : 'hover:bg-[#141416]/50'}`}>
      <div className="flex items-start gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown className="w-4 h-4 text-zinc-600 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-zinc-600 mt-0.5" />}
        <div className={`mt-1.5 w-2 h-2 rounded-full ${isSuccess ? 'bg-green-500' : 'bg-red-500'}`} />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-sm text-white">{span.name}</span>
            <span className={`text-xs ${isSuccess ? 'text-green-500' : 'text-red-500'}`}>{isSuccess ? '✓' : '✗'} {span.status_code || 'OK'}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            {model && <span className="font-mono">{model}</span>}
            {latency && <span>{latency}s</span>}
            {cost && <span>${parseFloat(cost).toFixed(4)}</span>}
          </div>
        </div>
      </div>
      {expanded && span.attributes && (
        <div className="mt-3 ml-9 p-3 bg-[#09090b] rounded-lg">
          <pre className="text-xs text-zinc-400 overflow-x-auto">{JSON.stringify(span.attributes, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

export function TraceDetailDrawer({ traceId, onClose }) {
  const { data: trace, isLoading, error } = useTrace(traceId);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const { model, provider } = extractModelFromTrace(trace);
  const isSuccess = trace?.outcome_success !== false;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-[#09090b] border-l border-white/[0.06] z-50 overflow-y-auto">
        <div className="sticky top-0 bg-[#09090b]/80 backdrop-blur-xl border-b border-white/[0.06] px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white mb-1">Trace Detail</h2>
              <code className="text-xs text-zinc-500 font-mono">{traceId}</code>
            </div>
            <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white hover:bg-white/[0.04] rounded-lg"><X className="w-5 h-5" /></button>
          </div>
          {trace && (
            <div className="flex items-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2 text-zinc-400"><Clock className="w-4 h-4" />{trace.total_latency ? `${trace.total_latency.toFixed(2)}s` : '—'}</div>
              <div className="flex items-center gap-2 text-zinc-400"><DollarSign className="w-4 h-4" />{trace.total_cost ? `$${trace.total_cost.toFixed(5)}` : '—'}</div>
              <div className={`flex items-center gap-2 ${isSuccess ? 'text-green-500' : 'text-red-500'}`}>
                {isSuccess ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}{isSuccess ? 'Success' : 'Failed'}
              </div>
            </div>
          )}
        </div>

        {isLoading && <div className="p-6 space-y-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-16 w-full" /></div>}
        {error && <div className="p-6"><div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center"><p className="text-red-500">Failed to load trace</p></div></div>}

        {trace && (
          <div className="p-6 space-y-6">
            <div className="bg-[#0f0f11] border border-white/[0.04] rounded-xl p-5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-4">Path Selected</div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center"><Zap className="w-6 h-6 text-blue-500" /></div>
                <div>
                  <div className="font-mono text-white font-medium">{model || 'Model not captured'}</div>
                  {provider && <div className="text-sm text-zinc-500">{provider}</div>}
                </div>
              </div>
            </div>

            {/* Modality-specific metrics */}
            {trace.modality && trace.modality !== '' && trace.modality !== 'text' && (
              <div className="bg-[#0f0f11] border border-white/[0.04] rounded-xl p-5">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-4">
                  {trace.modality === 'audio' && <><Mic className="w-3 h-3 inline mr-1" />Audio Details</>}
                  {trace.modality === 'image' && <><Image className="w-3 h-3 inline mr-1" />Image Details</>}
                  {trace.modality === 'embedding' && <><FileText className="w-3 h-3 inline mr-1" />Embedding Details</>}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {trace.audio_duration_ms > 0 && (
                    <div><span className="text-zinc-500">Duration</span><div className="text-white font-mono">{(trace.audio_duration_ms / 1000).toFixed(2)}s</div></div>
                  )}
                  {trace.audio_format && (
                    <div><span className="text-zinc-500">Format</span><div className="text-white font-mono">{trace.audio_format}</div></div>
                  )}
                  {trace.image_count > 0 && (
                    <div><span className="text-zinc-500">Images</span><div className="text-white font-mono">{trace.image_count}</div></div>
                  )}
                  {trace.image_resolution && (
                    <div><span className="text-zinc-500">Resolution</span><div className="text-white font-mono">{trace.image_resolution}</div></div>
                  )}
                  {trace.input_units > 0 && (
                    <div><span className="text-zinc-500">Input {trace.unit_type || 'units'}</span><div className="text-white font-mono">{trace.input_units}</div></div>
                  )}
                  {trace.output_units > 0 && (
                    <div><span className="text-zinc-500">Output {trace.unit_type || 'units'}</span><div className="text-white font-mono">{trace.output_units}</div></div>
                  )}
                </div>
              </div>
            )}

            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-4">Execution Steps ({trace.spans?.length || 0})</div>
              <div className="space-y-1">
                {trace.spans?.map((span, i) => <SpanItem key={span.span_id || i} span={span} defaultExpanded={i === 0} />)}
              </div>
            </div>

            {trace.outcome_success !== null && trace.outcome_success !== undefined && (
              <div className={`rounded-xl p-5 border ${trace.outcome_success ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
                <div className="flex items-center gap-3">
                  {trace.outcome_success ? <CheckCircle className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-red-500" />}
                  <div className={`font-semibold ${trace.outcome_success ? 'text-green-500' : 'text-red-500'}`}>
                    {trace.outcome_success ? 'Execution Succeeded' : 'Execution Failed'}
                  </div>
                </div>
                {trace.failure_category && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-zinc-500">Failure type:</span>
                    <span className="text-xs text-red-400 bg-red-400/10 px-2 py-0.5 rounded font-medium font-mono">{trace.failure_category}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default TraceDetailDrawer;
